// releaseData.ts - Centralized release notes data

export type ReleaseStatus = "DEMO" | "STABLE" | "DEV";

export interface Release {
  version: string;
  date: string;
  status: ReleaseStatus;
  environment: {
    frontend: string;
    backend: string;
    databases: string[];
  };
  summary: string;
  sections: {
    title: string;
    items: string[];
  }[];
  notes?: string[];
}

// =============================================================================
// RELEASE DATA - Add new releases at the TOP of this array
// =============================================================================

export const releases: Release[] = [
  // ---------------------------------------------------------------------------
  // Release 1.1 - LATEST
  // ---------------------------------------------------------------------------
  {
    version: "1.1.0",
    date: "09/02/2026",
    status: "DEMO",
    environment: {
      frontend: "https://tecnovation-pandora.xyz",
      backend: "https://api2.pandora-ai.it",
      databases: ["primo_rag", "smartenergy", "wpdemo"]
    },
    summary: "Maggiore affidabilità delle comunicazioni, continuità avanzata delle conversazioni, interazione più precisa tramite risposte contestuali.",
    sections: [
      {
        title: " Network Layer Hardening",
        items: [
          "Exponential backoff retry mechanism per chiamate LLM",
          "Gestione timeout con recupero automatico",
          "Connection pooling ottimizzato per sessioni prolungate",
          "Circuit breaker pattern per degradazione controllata",
          "Riduzione ~80% errori di risposta parziale",
          "95%+ completion rate su conversazioni lunghe"
        ]
      },
      {
        title: "Persistent State Memory Engine",
        items: [
          "Storage backend: Redis con TTL basato su sessione",
          "Context window: ultimi 10 messaggi + messaggi referenziati",
          "Compressione: summarization semantica per cronologie lunghe",
          "Retrieval: similarity search per selezione contesto rilevante",
          "Deduplicazione semantica previene recupero ridondante",
          "Decay mechanism: contesto più vecchio pesa meno nel prompt",
          "Thread coherence: mantiene flusso logico in conversazioni multi-turno"
        ]
      },
      {
        title: "Contextual Reply System",
        items: [
          "Schema DB: parent_message_id (uuid | null), is_contextual_reply (boolean)",
          "Risposta ancorata a messaggio specifico con relazione parent-child esplicita",
          "API: POST /storage/messages con parent_message_id",
          "Response include parent_context con query/SQL/risultati originali",
          "Use case: drill-down analysis, chiarimenti, thread ramificati, audit trail"
        ]
      },
      {
        title: "Adaptive Search Engine",
        items: [
          "LLM decide autonomamente se cercare fonti esterne (no classifier, no vector DB)",
          "Flag: use_web=true abilita valutazione dinamica",
          "Fattori di decisione: tipo semantico query, coverage DB, rilevanza temporale",
          "Fallback order: Internal DB → Web search → Combined",
          "Esempio routing: 'Q4 revenue' → solo RAG interno, 'industry trends' → web search",
          "Prefisso manuale @search  deprecato e rimosso"
        ]
      },
      {
        title: "Semantic Layer - Technical Deep Dive",
        items: [
          "Storage: JSON document per tenant con alias mapping",
          "Endpoints: GET/PUT /admin/semantic-layer/{tenant}",
          "Headers richiesti: Authorization Bearer + x-admin-token + If-Match (ETag)",
          "Optimistic locking via ETag previene conflitti di scrittura",
          "Errore 412 Precondition Failed su ETag mismatch",
          "Schema: meta {tenant, last_modified, etag} + data {aliases[{alias, canonical, field_type, description}]}",
          "Alias resolution: User Query (NL) → Semantic Layer → SQL Generator → Database"
        ]
      },
      {
        title: "Data Type Handling Fix",
        items: [
          "PROBLEMA RISOLTO: valori numerici (COUNT, aggregati) serializzati come stringhe",
          "Root cause: str(row['count']) invece di int(row['count'])",
          "Soluzione: preservare tipi nativi (int, float) attraverso JSON serialization",
          "Frontend riceve primitivi corretti per librerie charting",
          "Solo conversioni semantiche applicate (Wh → kWh)",
          "Impact: chart rendering corretto, no type coercion errors"
        ]
      },
      {
        title: "Y-Axis Metadata Enhancement",
        items: [
          "Implementazione attuale: rilevamento euristico basato su keyword",
          "Currency detection: 'costo/ricavo/importo/prezzo' → {unit: '€', format: 'it-IT'}",
          "Time detection: 'ore/ora/hours/hour' → {unit: 'h', format: 'number'}",
          "Energy detection: 'kwh' → {unit: 'kWh', format: 'number'}",
          "Chart config fornita da endpoint dedicato: POST /generate_chart",
          "Pianificato: schema metadata database-driven per eliminare keyword guessing"
        ]
      },
      {
        title: "Chat AI Request Flow",
        items: [
          "1. Frontend → POST /storage/messages (user message)",
          "2. Backend → INSERT user message in PostgreSQL",
          "3. Backend → Load tenant aliases da Semantic Layer",
          "4. Backend → POST /chat/{dbId} + state memory injection",
          "5. LLM → Apply aliases + Inject context + Generate SQL",
          "6. Backend → INSERT bot message in PostgreSQL",
          "7. Backend → Response con user_message_id, bot_message_id, SQL, results, explanation",
          "8. Separato: POST /generate_chart per configurazione grafici"
        ]
      }
    ],
    notes: [
      "Production-ready in ambiente DEMO",
      " Tutte le attività della settimana precedente completate e consegnate",
      "Allineamento macchina demo, numerazione release, database configurati",
      "Semantic Layer CamNgo attivo, report tabellari stabilizzati, PDF export",
      "Procedura debug e report periodici operativi",
      " State memory limitata a scope di sessione (no cross-session persistence)",
      "Web search dipende da disponibilità API esterne (no local cache)",
      "Y-axis metadata usa euristica; schema database-driven in roadmap",
      "ETag conflicts richiedono riconciliazione manuale in edit concorrenti"
    ]
  },

  // ---------------------------------------------------------------------------
  // Release 1.0.1
  // ---------------------------------------------------------------------------
  {
    version: "1.0.1",
    date: "03/02/2026",
    status: "DEMO",
    environment: {
      frontend: "https://tecnovation-pandora.xyz",
      backend: "https://api2.pandora-ai.it",
      databases: ["primo_rag", "smartenergy", "wpdemo"]
    },
    summary: "Miglioramenti all'affidabilità del sistema e alla continuità conversazionale.",
    sections: [
      {
        title: "Miglioramenti Principali",
        items: [
          "Network resilience: retry con backoff, gestione timeout",
          "State memory: contesto di sessione per chat multi-turno",
          "Reply contestuali: collegamenti parent-child tra messaggi",
          "Adaptive web search: il modello decide quando usare fonti esterne",
          "Grafici: configurazione da endpoint dedicato generate_chart"
        ]
      },
      {
        title: "Semantic Layer",
        items: [
          "Mappa termini in linguaggio naturale a campi schema canonici",
          "API admin con optimistic locking via ETag",
          "Refetch e riapplicazione in caso di conflitto"
        ]
      },
      {
        title: "Integrità Dati",
        items: [
          "Valori numerici preservati come numeri nativi",
          "Solo conversioni semantiche applicate (Wh a kWh)"
        ]
      },
      {
        title: "Funzionalità Attuali",
        items: [
          "Natural language a SQL con semantic layer",
          "Supporto multi-database (primo_rag, smartenergy, wpdemo)",
          "Tabelle interattive con export PDF/Excel",
          "Grafici auto-generati con configurazione dedicata",
          "Preferiti e rating risposte AI",
          "Controllo accessi granulare e pannello admin",
          "UI multi-lingua (IT/FR/EN)",
          "Ricerca e gestione conversazioni",
          "Dashboard analytics per admin"
        ]
      }
    ],
    notes: [
      "Production-ready in ambiente DEMO",
      "Prefisso manuale @search disponibile ma deprecato",
      "State memory limitata allo scope di sessione",
      "Web search dipende da API esterne"
    ]
  }
];

// =============================================================================
// HOW TO ADD A NEW RELEASE:
// =============================================================================
// 1. Copy the template below
// 2. Fill in your data
// 3. Add it to the TOP of the releases array above
// 4. Done!
//
// TEMPLATE:
// {
//   version: "1.2",
//   date: "17/02/2026",
//   status: "DEMO",
//   environment: {
//     frontend: "https://tecnovation-pandora.xyz",
//     backend: "https://api2.pandora-ai.it",
//     databases: ["primo_rag", "smartenergy", "wpdemo"]
//   },
//   summary: "Short one-line description of this release",
//   sections: [
//     {
//       title: "Feature Name",
//       items: [
//         "Bullet point 1",
//         "Bullet point 2"
//       ]
//     }
//   ],
//   notes: [
//     "Optional note 1",
//     "Optional note 2"
//   ]
// }